// src/pages/Admin/Notifications/context/index.tsx

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

import { useSnackbar } from "notistack";

import { Box } from "@mui/material";

import Layout from "@components/Layout";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { useUserState } from "@context/UserContext";
import { Types } from "@ioc/types";

import { IReadConfirmation, NotificationTabKey } from "../model";
import {
  INotification,
  INotificationCreateDTO,
  INotificationFilter,
  INotificationService,
  IPaginatedNotifications,
  NotificationStatus,
} from "@modules/notifications/model/INotifications";

import { ACTIVE } from "../resource/tabs/tabs.header";

interface NotificationContextValue {
  loading: boolean;
  data: IPaginatedNotifications | null;
  currentTab: NotificationTabKey;
  filters: INotificationFilter;

  setTab: (tab: NotificationTabKey) => void;

  actions: {
    edit: (uuid: string, payload: INotificationCreateDTO) => Promise<void>;
    create: (payload: INotificationCreateDTO, creatBy: string) => Promise<void>;
    readById: (id: number) => Promise<INotification | undefined | null>;
    search: (filters?: Partial<INotificationFilter>) => Promise<void>;

    softDelete: (uuid: string | number) => Promise<void>;
    onReadConfirmation: (uuid: string | number) => Promise<IReadConfirmation[]>;
    resetMocks: () => Promise<void>;
    cleanModuleMock: () => Promise<void>;
  };
}

const NotificationContext = createContext<NotificationContextValue>(
  {} as NotificationContextValue
);

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider: React.FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();
  const userState = useUserState();

  const notificationService = useMemo(
    () =>
      iocContext.serviceContainer.get<INotificationService>(
        Types.Notifications.INotificationService
      ),
    [iocContext.serviceContainer]
  );

  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<NotificationTabKey>(ACTIVE);
  const [data, setData] = useState<IPaginatedNotifications | null>(null);

  const [filters, setFilters] = useState<INotificationFilter>({
    status: ACTIVE,
    page: 1,
    pageSize: 10,
  });

  const search = useCallback(
    async (extra?: Partial<INotificationFilter>) => {
      setLoading(true);

      try {
        const merged: INotificationFilter = {
          ...filters,
          ...extra,
        };

        const result = await notificationService.list(merged);
        setData(result);
        setFilters(merged);
      } finally {
        setLoading(false);
      }
    },
    [filters, notificationService]
  );

  const setTab = useCallback(
    (tab: NotificationTabKey) => {
      setCurrentTab(tab);

      // NOTE: Ao trocar de aba, resetamos página para 1
      const status: NotificationStatus = tab;
      void search({ status, page: 1 });
    },
    [search]
  );

  const create = useCallback(
    async (payload, createBy) => {
      try {
        await notificationService.create(payload, createBy);
        const successCreateMessage = `Notificação criada com sucesso`;
        enqueueSnackbar(successCreateMessage, { variant: "success" });
      } catch (error) {
        const errorCreateMessage = "Erro ao criar notificação";
        enqueueSnackbar(errorCreateMessage, { variant: "error" });
        throw error;
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const edit = useCallback(
    async (uuid, payload) => {
      try {
        await notificationService.update(Number(uuid), payload);
        const successEditMessage = "Notificação atualizada com sucesso";
        enqueueSnackbar(successEditMessage, { variant: "success" });
      } catch (error) {
        const errorEditMessage = "Erro ao editar notificação";
        enqueueSnackbar(errorEditMessage, { variant: "error" });
        throw error;
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const readById = useCallback(
    async (uuid) => {
      try {
        return await notificationService.getById(uuid);
      } catch (error) {
        const errorReadMessage = `Erro ao buscar notificação ${uuid}`;
        enqueueSnackbar(errorReadMessage, { variant: "error" });
      }
    },
    [notificationService, enqueueSnackbar]
  );

  const softDelete = useCallback(
    async (uuid) => {
      try {
        await notificationService.softDelete(Number(uuid), userState.username);
        const successDeleteMessage = `Notificação ${uuid} excluida com sucesso`;
        enqueueSnackbar(successDeleteMessage, { variant: "success" });

        // NOTE: Depois de excluir, recarregar aba atual
        await search({});
      } catch (error) {
        const errorDeleteMessage = `Erro ao excluir notificaçao ${uuid}`;
        enqueueSnackbar(errorDeleteMessage, { variant: "error" });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notificationService, search, userState.username]
  );

  const onReadConfirmation = useCallback((uuid: string | number) => {
    try {
      return notificationService.listReads(Number(uuid));
    } catch (error) {
      const errorReadMessage = `Erro ao buscar notificações lidas`;
      enqueueSnackbar(errorReadMessage, { variant: "error" });
      throw error;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetMocks = useCallback(async () => {
    await notificationService.resetMocks();

    // NOTE: depois de resetar, recarrega a aba atual a partir da página 1
    await search({ page: 1 });
  }, [notificationService, search]);

  const cleanModuleMock = async () => {
    await notificationService.clearNotificationsModule();
    await search({ page: 1 });
  };

  const value: NotificationContextValue = {
    loading,
    data,
    currentTab,
    filters,
    setTab,

    actions: {
      create,
      edit,
      readById,
      search,

      softDelete,
      onReadConfirmation,

      resetMocks,
      cleanModuleMock,
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      <Layout disableToolbar>
        <Box
          sx={{
            m: { xs: 0.5, md: 0.8, lg: "0px 32px 0px 32px" },
            p: { xs: 0.5, lg: "0px 0px 32px 0px" },
          }}
        >
          {children}
        </Box>
      </Layout>
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider"
    );
  }

  return context;
};
